/**
 * agentCapabilities — the CLIENT-SIDE reading of the member list the backend
 * already resolved.
 *
 * A template declares a member TWO ways, and installation has never told them
 * apart: `dispatchesWorkflow` ("I START this agent") and `marketplace.requires`
 * ("I CONSUME a surface this agent PUBLISHES"). Both produce the same deploy
 * plan, the same row, the same `childBindings` stamp, the same delete cascade.
 * Only RENDERING told them apart — by reading just one of them — so a
 * `requires` member was installed, bound, credential-bearing, and on NO SCREEN.
 *
 * ⚠️ THERE IS NO DERIVATION HERE, DELIBERATELY. The join (declaration → binding
 * ledger → row → recursion) happens ONCE, server-side, in
 * `backend/src/services/workflow-composition.js`, and arrives on the workflow
 * detail response as:
 *
 *   workflow.members      — this agent's own members, both kinds
 *   workflow.capabilities — the ROLLUP: every `requires` member declared
 *                           anywhere beneath this agent, each carrying
 *                             depth 0  → declared by THIS agent
 *                             depth ≥1 → declared by a MEMBER, with `owner`
 *                           plus `uuid` / `workflowType` / `displayName` of the
 *                           row that backs it, and `use` (see below).
 *
 * This app and the self-host dashboard share zero code, so a join rule
 * implemented in both would be a pair with nothing to fail on drift. Keeping
 * the derivation on the server is what makes that impossible rather than
 * merely tested. Everything below is presentation.
 */

/** Every capability in the rollup (own + inherited), safely. */
export function capabilitiesOf(workflow) {
  const list = workflow && Array.isArray(workflow.capabilities) ? workflow.capabilities : [];
  return list.filter((m) => m && m.slug);
}

/** Declared by THIS agent — the ones it can actually use. */
export function ownCapabilities(workflow) {
  return capabilitiesOf(workflow).filter((m) => !m.depth);
}

/**
 * Held by an agent BELOW this one. Never a capability claim for the parent: it
 * cannot call its member's endpoint — permissions are resolved per ROW from
 * that row's own envelope. It is drawn hanging off the CHILD's own card on this
 * canvas, which says precisely whose it is, and opens the row that owns it.
 */
export function inheritedCapabilities(workflow) {
  return capabilitiesOf(workflow).filter((m) => !!m.depth);
}

/**
 * The MCP servers a PERSON added to this agent by hand — everything in the
 * row's `customMcp` that the platform did not put there itself.
 *
 * DEDUPE: an entry the platform attached for a DECLARED member carries
 * `managedBy: 'requires:<as>'` (MAGNUM's KB entry is `requires:kb`). Those ARE
 * declared members and are already rendered from `capabilities`, so drawing
 * them again from this list would double them. The stamp is the whole test —
 * one field, written by the code that attaches them.
 */
export function handAddedMcp(workflow) {
  const list = workflow && Array.isArray(workflow.customMcp) ? workflow.customMcp : [];
  return list.filter((s) => s && !s.managedBy);
}

/** The name the product uses for this agent — never its slug, never its alias. */
export function capName(m) {
  return (m && (m.displayName || m.workflowType || m.slug)) || '';
}

/**
 * Which node ids declared they consume `m` (`uses: ['<alias>']`), limited to
 * nodes actually on this canvas. Empty is a legitimate, common state: it means
 * the member is attached at AGENT level and nothing has said more than that —
 * which is why an empty result must never be drawn with a connector to some
 * chosen node. A line saying "this node uses it" would be a false statement.
 */
export function capConsumers(m, nodeIdSet) {
  const list = m && Array.isArray(m.nodes) ? m.nodes : [];
  return nodeIdSet ? list.filter((id) => nodeIdSet.has(id)) : list;
}

/**
 * The route to the row that OWNS a capability — which for an inherited one is a
 * DIFFERENT agent than the page you are on. That row is where its model and its
 * credential live, so it is the only place the problem a warning chip is
 * flagging can be fixed.
 *
 * Addressed by `workflowType`, because that is what this app's router takes.
 * '' when the member is declared but not installed — the caller renders a chip
 * that says so instead of a dead link.
 */
export function capRoute(projectId, m) {
  if (!m || !m.workflowType || !projectId) return '';
  return `/projects/${encodeURIComponent(projectId)}/agents/${encodeURIComponent(m.workflowType)}`;
}

/**
 * The readiness key for a capability. Its UUID, never its slug: an INHERITED
 * capability is not in this agent's binding ledger at all, and a member whose
 * bare slug was taken is installed under a namespaced one — so a slug lookup
 * either misses it or lands on a same-named stranger.
 */
export function capKey(m) {
  return (m && (m.uuid || m.slug)) || '';
}
